import { json, type DataFunctionArgs } from '@remix-run/node'
import { useState } from 'react'
import { useFetcher } from '@remix-run/react'
import { ErrorList } from '~/components/forms.tsx'
import { useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { z } from 'zod'
import { requireUserId, getUsername } from '~/utils/auth.server.ts'
import { prisma } from '~/utils/db.server.ts'
import { Button } from '~/components/ui/button.tsx'
import { StatusButton } from '~/components/ui/status-button.tsx'
import { redirectWithToast } from '~/utils/flash-session.server.ts'
import { Icon } from '~/components/ui/icon.tsx'

const DeleteFormSchema = z.object({
	linkId: z.string(),
})

export async function action({ request }: DataFunctionArgs) {
	await requireUserId(request)
	const username = await getUsername(request)
	const formData = await request.formData()
	const submission = parse(formData, {
		schema: DeleteFormSchema,
		acceptMultipleErrors: () => true,
	})
	if (!submission.value || submission.intent !== 'submit') {
		return json(
			{
				status: 'error',
				submission,
			} as const,
			{ status: 400 },
		)
	}

	const { linkId } = submission.value

	const link = await prisma.link.findUnique({
		select: { id: true },
		where: {
			id: linkId,
		},
	})
	if (!link) {
		submission.error.linkId = ['link not found']
		return json({ status: 'error', submission } as const, {
			status: 404,
		})
	}

	await prisma.link.delete({
		where: { id: link.id },
	})

	return redirectWithToast(`/${username}/links`, {
		title: 'Dun, dun, dun, another on bites the dust...',
		variant: 'destructive',
	})
}

export function DeleteLinkButton({ linkId }: { linkId: string }) {
	const linkDeleteFetcher = useFetcher<typeof action>()

	const [isDeleteActive, setIsDeleteActive] = useState(false)

	const [form] = useForm({
		id: 'delete-link',
		constraint: getFieldsetConstraint(DeleteFormSchema),
		onValidate({ formData }) {
			return parse(formData, { schema: DeleteFormSchema })
		},
	})

	const handleMouseOut = () => {
		setTimeout(() => {
			setIsDeleteActive(false)
		}, 2000)
	}

	return (
		<>
			{!isDeleteActive ? (
				<Button
					type="button"
					size="sm"
					variant="destructive"
					onClick={() => setIsDeleteActive(true)}
					className="px-2 text-background"
				>
					<Icon size="lg" name="trash" />
				</Button>
			) : (
				<linkDeleteFetcher.Form
					method="post"
					action="/resources/delete-link"
					{...form.props}
					className="inline-block"
				>
					<input type="hidden" name="linkId" value={linkId} />
					<StatusButton
						type="submit"
						size="sm"
						variant="default"
						status={
							linkDeleteFetcher.state === 'submitting'
								? 'pending'
								: linkDeleteFetcher.data?.status ?? 'idle'
						}
						disabled={linkDeleteFetcher.state !== 'idle'}
						onMouseOut={handleMouseOut}
					>
						Are you sure?
					</StatusButton>
					<ErrorList errors={form.errors} id={form.errorId} />
				</linkDeleteFetcher.Form>
			)}
		</>
	)
}
